import React, { useState, useRef, useEffect, useCallback } from 'react';
import './AddressAutocomplete.css';

interface AddressAutocompleteProps {
  value: string;
  onChange: (address: string) => void;
  onPlaceSelect: (address: string, coordinates: [number, number], city?: string, postalCode?: string, isSpecificAddress?: boolean) => void;
  onClear?: () => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  searchTypes?: string[];
  inputId?: string;
}

// Bias results toward the GTA (a bias, not a hard restriction)
const GTA_BOUNDS = { south: 43.1, west: -80.7, north: 44.5, east: -78.4 };

// Places API (New) doesn't support the legacy 'address' type collection,
// so map it to the closest primary types. Other values pass through unchanged
// (e.g. '(cities)', 'locality').
function toPrimaryTypes(searchTypes: string[]): string[] {
  const out: string[] = [];
  for (const t of searchTypes) {
    if (t === 'address') out.push('street_address', 'subpremise', 'premise');
    else if (t === 'geocode' || t === 'establishment') continue; // not valid in the new API
    else out.push(t);
  }
  return out.slice(0, 5); // API allows at most 5
}

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onPlaceSelect,
  onClear,
  placeholder = 'Search address…',
  required = false,
  disabled = false,
  searchTypes = ['address'],
  inputId,
}) => {
  const [predictions, setPredictions] = useState<google.maps.places.PlacePrediction[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0); // ignore out-of-order responses

  const newSessionToken = () => {
    const places = window.google?.maps?.places;
    if (places?.AutocompleteSessionToken) {
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }
  };

  const searchTypesRef = useRef(searchTypes);
  useEffect(() => { searchTypesRef.current = searchTypes; }, [searchTypes]);

  const fetchPredictions = useCallback(async (input: string) => {
    const places = window.google?.maps?.places;
    if (!places?.AutocompleteSuggestion) return;
    if (!sessionTokenRef.current) newSessionToken();

    const myRequestId = ++requestIdRef.current;

    try {
      const primaryTypes = toPrimaryTypes(searchTypesRef.current);
      const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        sessionToken: sessionTokenRef.current ?? undefined,
        includedRegionCodes: ['ca'],
        ...(primaryTypes.length > 0 ? { includedPrimaryTypes: primaryTypes } : {}),
        locationBias: GTA_BOUNDS,
      });

      if (myRequestId !== requestIdRef.current) return; // a newer request superseded this one

      const results = suggestions
        .map(s => s.placePrediction)
        .filter((p): p is google.maps.places.PlacePrediction => !!p);

      setPredictions(results);
      setIsOpen(results.length > 0);
    } catch (err) {
      if (myRequestId !== requestIdRef.current) return;
      console.error('Address autocomplete failed:', err);
      setPredictions([]);
      setIsOpen(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange(val);
    setActiveIndex(-1);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (val.length < 3) {
      requestIdRef.current++; // cancel any in-flight result
      setPredictions([]);
      setIsOpen(false);
      return;
    }

    debounceRef.current = setTimeout(() => fetchPredictions(val), 300);
  };

  const handleFocus = () => {
    if (!sessionTokenRef.current) newSessionToken();
  };

  const handleSelect = async (prediction: google.maps.places.PlacePrediction) => {
    const description = prediction.text.text;

    setPredictions([]);
    setIsOpen(false);
    setActiveIndex(-1);
    requestIdRef.current++;

    try {
      // toPlace() carries the session token from the autocomplete requests,
      // so these calls are billed together as one session.
      const place = prediction.toPlace();
      await place.fetchFields({
        fields: ['location', 'formattedAddress', 'addressComponents'],
      });

      if (place.location) {
        const lat = place.location.lat();
        const lng = place.location.lng();
        const addr = place.formattedAddress ?? description;

        let city: string | undefined;
        let postalCode: string | undefined;
        let isSpecificAddress = false;
        for (const comp of place.addressComponents ?? []) {
          if (comp.types.includes('locality')) city = comp.longText ?? undefined;
          if (comp.types.includes('postal_code')) postalCode = comp.longText ?? undefined;
          if (comp.types.includes('street_number')) isSpecificAddress = true;
        }

        onChange(addr);
        onPlaceSelect(addr, [lng, lat], city, postalCode, isSpecificAddress);
      }
    } catch (err) {
      console.error('Failed to fetch place details:', err);
    } finally {
      newSessionToken(); // session ends after a selection
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      setIsOpen(false);
      setActiveIndex(-1);
    }, 150);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || predictions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, predictions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(predictions[activeIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const handleClear = () => {
    onChange('');
    setPredictions([]);
    setIsOpen(false);
    requestIdRef.current++;
    onClear?.();
  };

  return (
    <div className="address-autocomplete">
      <input
        id={inputId}
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls="address-autocomplete-listbox"
        className={value ? 'has-value' : ''}
      />
      {value && !disabled && (
        <button
          type="button"
          className="address-clear-btn"
          onMouseDown={e => { e.preventDefault(); handleClear(); }}
          aria-label="Clear address"
        >✕</button>
      )}

      {isOpen && predictions.length > 0 && (
        <ul id="address-autocomplete-listbox" className="address-autocomplete-dropdown" role="listbox">
          {predictions.map((pred, index) => (
            <li
              key={pred.placeId}
              role="option"
              aria-selected={index === activeIndex}
              className={index === activeIndex ? 'active' : ''}
              onMouseDown={(e) => {
                e.preventDefault(); // prevent blur firing before click
                handleSelect(pred);
              }}
            >
              <span className="autocomplete-main">{pred.mainText?.text ?? pred.text.text}</span>
              <span className="autocomplete-secondary">{pred.secondaryText?.text ?? ''}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
